import { useCallback, useEffect, useState } from 'react';

import {
  MAX_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
} from '@/contexts/ChatSidebarContext';

interface UseResizableSidebarOptions {
  onWidthChange: (width: number) => void;
}

const constrainWidth = (width: number): number => {
  const maxAllowed = Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth * 0.5);

  return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), maxAllowed);
};

export const useResizableSidebar = ({
  onWidthChange,
}: UseResizableSidebarOptions) => {
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      onWidthChange(constrainWidth(newWidth));
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const newWidth = window.innerWidth - touch.clientX;
        onWidthChange(constrainWidth(newWidth));
      }
    };

    const handleEnd = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [isResizing, onWidthChange]);

  return {
    isResizing,
    handleMouseDown,
    handleTouchStart,
  };
};
